// Modules
import { useEffect, useState } from "react"
import { useForm, FormProvider } from "react-hook-form"

// Constants
import { STATUS } from "~constants/status"
import { ERROR_MESSAGES } from "~constants/errorMessages"

// RBAC
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import isCompanyRole from "~rbac/utils/isCompanyRole"
import checkAuthorization from "~rbac/utils/checkAuthorization"

// Context
import { useAuthContext } from "~context/authContext"

// Utils
import {
  step1PrefillValues,
  step2PrefillValues,
} from "~components/messages/utils"

// Components
import Modal from "~app/Modal"
import MessageModalStep1 from "~components/messages/MessageModalStep1"
import MessageModalStep2 from "~components/messages/MessageModalStep2"
import { ErrorMessageSmall } from "~styled/errors"
import { WizardContainer, WizardForm } from "~styled/wizard"

// Services
import CompanyService from "~services/CompanyService"
import MessageService from "~services/MessageService"

// Interfaces
import { CompanyShape } from "~shapes/company"
import { OnRequestCloseProps } from "~shapes/modal"
import { MessageShape, PostMessageShape } from "~shapes/message"
import moment from "moment"
import { processImage } from "~utils/utils"

interface MessageModalProps {
  onRequestClose: (props: OnRequestCloseProps) => void
  message?: MessageShape
}

const MessageModal = ({ onRequestClose, message }: MessageModalProps) => {
  // Get from context
  const { loggedCompany } = useAuthContext()

  const [formData, setFormData] = useState<PostMessageShape>()
  const [companies, setCompanies] = useState<CompanyShape[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [stepNumber, setStepNumber] = useState<number>(1)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [nextButtonLabel, setNextButtonLabel] = useState<string>("Next")
  const [prevButtonLabel, setPrevButtonLabel] = useState<string>("Cancel")
  const [canUpdateCompany, setCanUpdateCompany] = useState<boolean>(false)

  const step1Methods = useForm()
  const step2Methods = useForm()

  const companyRequestParams = { sort: "name", size: 100 }

  // Effect to check user authorizations
  useEffect(() => {
    const canUpdateCompany = checkAuthorization(
      RBAC_RULES.UPDATE_MESSAGE_COMPANY
    )
    setCanUpdateCompany(canUpdateCompany)
  }, [])

  // Effect to fetch all companies
  useEffect(() => {
    if (canUpdateCompany) {
      fetchCompanies()
    }
  }, [canUpdateCompany])

  // Effect to handle formData changes
  useEffect(() => {
    ;(() => {
      if (formData) {
        // Move forward
        if (stepNumber === 1) {
          moveToNextStep()
        }
        // Create the Message
        else {
          handleSave(formData)
        }
      }
    })()
  }, [formData])

  // Effect to fill form with an existing Message
  useEffect(() => {
    if (message) {
      step1Methods.reset(step1PrefillValues(message))
      step2Methods.reset(step2PrefillValues(message))
    }
  }, [message])

  // Effect to handle the modal button labels
  useEffect(() => {
    if (stepNumber === 1) {
      setPrevButtonLabel("Cancel")
      setNextButtonLabel("Next")
    } else {
      setPrevButtonLabel("Previous")
      setNextButtonLabel(message ? "Update" : "Create")
    }
  }, [stepNumber])

  const fetchCompanies = async () => {
    setIsLoading(true)

    try {
      const response = await CompanyService.getCompanies(companyRequestParams)
      setCompanies(response._embedded.companies)
    } catch (err) {
      setCompanies([])
    }

    setIsLoading(false)
  }

  const handleSave = async formData => {
    let company: CompanyShape | null = loggedCompany
    let coverImageUrl: string = ""

    if (!isCompanyRole()) {
      company = companies.find(el => el.id === formData.company?.value) || null
    }

    if (!company) {
      setErrorMessage(ERROR_MESSAGES.REQUIRED_COMPANY)
      return
    }
    setIsLoading(true)

    try {
      // Upload Cover just if it's needed (new upload, type "object")
      coverImageUrl = await processImage(
        "message",
        formData.cover,
        "cover",
        formData.code
      )
    } catch (err) {
      setErrorMessage(err.message)
      setIsLoading(false)
    }

    const messagePayload: PostMessageShape = {
      id: message ? message.id : undefined,
      code: formData.code,
      name: formData.name,
      description: formData.description,
      status: STATUS.ACTIVE,
      company: company?._links?.self.href || "",
      cover: coverImageUrl,
      publishedAt: moment(new Date(formData.publishedAt)).format(
        "YYYY-MM-DD hh:mm:ss"
      ),
    }

    try {
      let response: any

      if (message) response = await MessageService.patchMessage(messagePayload)
      else response = await MessageService.postMessage(messagePayload)

      setErrorMessage("")
      onRequestClose({ shouldRefreshList: true, data: response })
    } catch (err) {
      setErrorMessage(err.message)
      setIsLoading(false)
    }
  }

  const moveToNextStep = () => {
    setStepNumber(stepNumber + 1)
  }

  const onFormSubmit = data => {
    setFormData({ ...formData, ...data })
  }

  const onMoveStepForward = () => {
    if (stepNumber === 1) {
      step1Methods.handleSubmit(onFormSubmit)()
    } else {
      step2Methods.handleSubmit(onFormSubmit)()
    }
  }

  const onMoveStepBack = () => {
    const newStepVal = stepNumber - 1
    setStepNumber(newStepVal)

    if (newStepVal < 1) {
      onRequestClose({ shouldRefreshList: false })
    }
  }

  return (
    <Modal
      isOpen={true}
      scrollable={true}
      buttonLabel={nextButtonLabel}
      cancelLabel={prevButtonLabel}
      cancelButton={!isLoading}
      isButtonLoading={isLoading}
      onRequestClose={() => onRequestClose({ shouldRefreshList: false })}
      onRequestCancel={onMoveStepBack}
      onRequestConfirm={onMoveStepForward}
    >
      <WizardContainer>
        <WizardForm>
          <FormProvider {...step1Methods}>
            <MessageModalStep1
              companies={companies}
              isVisible={stepNumber === 1}
              isLoading={isLoading}
            />
          </FormProvider>

          <FormProvider {...step2Methods}>
            <MessageModalStep2 isVisible={stepNumber === 2} />
          </FormProvider>

          <ErrorMessageSmall style={{ textAlign: "right" }}>
            {errorMessage}
          </ErrorMessageSmall>
        </WizardForm>
      </WizardContainer>
    </Modal>
  )
}

export default MessageModal
